import { cn } from "@/shared/lib/cn";
import { messages } from "@/shared/i18n";

interface CardSkeletonProps {
  /** Megjelenítendő skeleton sorok száma */
  lines?: number;
  /** Fejléc skeleton megjelenítése */
  showHeader?: boolean;
  /** Gomb skeleton megjelenítése */
  showButton?: boolean;
  /** Egyedi className */
  className?: string;
}

/**
 * Card Skeleton komponens betöltési állapot jelzésére.
 *
 * A Card komponens layout-jához igazított skeleton,
 * ami jobb UX-t biztosít mint egy egyszerű spinner.
 *
 * Használat:
 * ```tsx
 * <Suspense fallback={<CardSkeleton />}>
 *   <LazyComponent />
 * </Suspense>
 *
 * // Testreszabott skeleton
 * <CardSkeleton lines={4} showHeader showButton />
 * ```
 */
export function CardSkeleton({
  lines = 3,
  showHeader = true,
  showButton = true,
  className,
}: CardSkeletonProps) {
  return (
    <div
      aria-busy="true"
      aria-label={messages.components.accessibility.loading}
      className={cn("shadow-card animate-pulse rounded-2xl bg-white p-8", className)}
      role="status"
    >
      {/* Fejléc skeleton */}
      {showHeader && (
        <div className="mb-8 flex flex-col items-center gap-2">
          {/* Cím */}
          <div className="h-8 w-48 rounded-lg bg-gray-200" />
          {/* Alcím */}
          <div className="h-4 w-64 rounded bg-gray-200" />
        </div>
      )}

      {/* Tartalom skeleton - sorok */}
      <div className="space-y-4">
        {Array.from({ length: lines }).map((_, index) => (
          <div key={index} className="space-y-2">
            {/* Label skeleton */}
            <div className="h-4 w-24 rounded bg-gray-200" />
            {/* Input skeleton */}
            <div className="h-10 w-full rounded-lg bg-gray-200" />
          </div>
        ))}
      </div>

      {/* Gomb skeleton */}
      {showButton && <div className="mt-6 h-12 w-full rounded-lg bg-gray-200" />}

      {/* Link skeleton */}
      <div className="mt-6 flex justify-center">
        <div className="h-4 w-40 rounded bg-gray-200" />
      </div>
    </div>
  );
}
